import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Shield, Settings, BarChart3, Target } from 'lucide-react';
import { useAnalyticsConsent } from '@/hooks/useAnalyticsConsent';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const PrivacyConsentBanner: React.FC = () => {
  const { consent, updateConsent, isLoading } = useAnalyticsConsent();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [tempConsent, setTempConsent] = useState({
    analytics_consent: false,
    performance_consent: false,
    marketing_consent: false,
  });

  // Only hide once an actual decision exists (or this session's decision was made).
  // No decision => everything stays denied (Consent Mode v2 defaults).
  if (isLoading || dismissed) return null;
  if (consent !== null && consent !== undefined) return null;

  const handleAcceptAll = () => {
    updateConsent({
      analytics_consent: true,
      performance_consent: true,
      marketing_consent: true,
    });
    setDismissed(true);
  };

  const handleRejectAll = () => {
    updateConsent({
      analytics_consent: false,
      performance_consent: false,
      marketing_consent: false,
    });
    setDismissed(true);
  };

  const handleSavePreferences = () => {
    updateConsent(tempConsent);
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <Card className="border-border bg-card shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <Shield className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div>
                <h3 className="font-semibold text-foreground mb-2">{t('consentBanner.title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('consentBanner.body')}
                </p>
              </div>
            </div>

            {showDetails && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="space-y-4 mb-4 overflow-hidden"
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">{t('consentBanner.analytics')}</span>
                      <Badge variant="secondary" className="text-xs">{t('consentBanner.optional')}</Badge>
                    </div>
                    <Checkbox
                      checked={tempConsent.analytics_consent}
                      onCheckedChange={(checked) =>
                        setTempConsent((prev) => ({ ...prev, analytics_consent: !!checked }))
                      }
                    />
                  </div>
                  <p className="text-xs text-muted-foreground pl-6">
                    {t('consentBanner.analyticsDesc')}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">{t('consentBanner.performance')}</span>
                      <Badge variant="secondary" className="text-xs">{t('consentBanner.optional')}</Badge>
                    </div>
                    <Checkbox
                      checked={tempConsent.performance_consent}
                      onCheckedChange={(checked) =>
                        setTempConsent((prev) => ({ ...prev, performance_consent: !!checked }))
                      }
                    />
                  </div>
                  <p className="text-xs text-muted-foreground pl-6">
                    {t('consentBanner.performanceDesc')}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">{t('consentBanner.marketing')}</span>
                      <Badge variant="secondary" className="text-xs">{t('consentBanner.optional')}</Badge>
                    </div>
                    <Checkbox
                      checked={tempConsent.marketing_consent}
                      onCheckedChange={(checked) =>
                        setTempConsent((prev) => ({ ...prev, marketing_consent: !!checked }))
                      }
                    />
                  </div>
                  <p className="text-xs text-muted-foreground pl-6">
                    {t('consentBanner.marketingDesc')}
                  </p>
                </div>

                <Button
                  variant="secondary"
                  onClick={handleSavePreferences}
                  className="w-full min-h-[44px]"
                >
                  {t('consentBanner.save')}
                </Button>
              </motion.div>
            )}

            <div className="flex gap-2">
              <Button onClick={handleAcceptAll} className="flex-1 min-h-[44px]">
                {t('consentBanner.acceptAll')}
              </Button>
              <Button variant="outline" onClick={handleRejectAll} className="flex-1 min-h-[44px]">
                {t('consentBanner.rejectAll')}
              </Button>
            </div>

            <div className="flex items-center justify-between mt-3">
              <button
                type="button"
                onClick={() => setShowDetails((v) => !v)}
                className="text-xs text-muted-foreground underline min-h-[44px] whitespace-nowrap"
              >
                {showDetails ? t('consentBanner.hide') : t('consentBanner.customise')}
              </button>
              <div className="flex items-center gap-3 flex-wrap justify-end">
                <button
                  type="button"
                  onClick={() => navigate('/cookies')}
                  className="text-xs text-muted-foreground underline min-h-[44px] whitespace-nowrap"
                >
                  {t('consentBanner.cookiePolicy')}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/privacy')}
                  className="text-xs text-muted-foreground underline min-h-[44px] whitespace-nowrap"
                >
                  {t('consentBanner.privacyPolicy')}
                </button>
              </div>
            </div>

          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};
